import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { CourseForm } from "./components/CourseForm";
import {
  useGetCourseQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
} from "../query/coursesQuery";
import type { RestCreateCourse } from "@KassaroAB/shared/types/course";

const FORM_ID = "catalog-course-form";

export const CourseFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("courses");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const courseId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: course,
    isLoading,
    error: loadError,
  } = useGetCourseQuery(scope, courseId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateCourseMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateCourseMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteCourseMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!courseId && !isLoading && !course;

  const handleSubmit = (data: RestCreateCourse) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!courseId) return;
    updateMutation(
      { courseId, course: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("coursesView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!courseId) return;
    deleteMutation(courseId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("coursesView.panelCreate")
    : (course?.name ?? t("coursesView.title"));
  const breadcrumbCurrent = isCreating
    ? t("coursesView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (course?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!course && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("coursesView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("coursesView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("coursesView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !course ? (
          <LoadingContainer message={t("coursesView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <CourseForm
              key={course?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              course={isCreating ? undefined : course}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("courseForm.deleteCourse")}
        message={t("coursesView.deleteConfirm", {
          name: course?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
